import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  numberSchemes, numberExample, DEFAULT_NUMBER_FORMAT, type NumberScheme, type ResetPeriod,
} from '@/api/numberSchemes';
import { orgs } from '@/api/catalogs';
import { useConfirm } from '@/components/ConfirmProvider';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

interface Editing {
  id?: string;
  document_type: string;
  organization_id: string | null;
  prefix: string;
  include_year: boolean;
  year_digits: 2 | 4;
  include_month: boolean;
  include_org_code: boolean;
  counter_width: number;
  separator: string;
  reset_period: ResetPeriod;
  per_organization: boolean;
  is_active: boolean;
}

// Локалізовані назви типів документів для дропдауна.
// Згруповано за категоріями для зручності пошуку.
const DOC_TYPE_LABELS: Record<string, string> = {
  // ===== Каса / Банк =====
  'doc.cash_receipts': 'ПКО (прибутковий касовий)',
  'doc.cash_payments': 'РКО (видатковий касовий)',
  'doc.bank_receipts': 'Банк — надходження',
  'doc.bank_payments': 'Банк — платежі',
  'doc.payment_orders': 'Платіжний ордер',
  'doc.currency_purchase_sale': 'Купівля / продаж валюти',
  'doc.advance_reports': 'Авансовий звіт',
  'doc.manual_operations': 'Бухгалтерська довідка',
  'doc.fx_revaluations': 'Переоцінка валюти',

  // ===== Товари / Склад =====
  'doc.goods_receipts': 'Прибуткова накладна',
  'doc.goods_sales': 'Видаткова накладна',
  'doc.service_acts': 'Акт виконаних робіт/послуг',
  'doc.tnn': 'Товарно-транспортна накладна (ТТН)',
  'doc.goods_transfers': 'Переміщення товарів',
  'doc.goods_writeoffs': 'Акт списання товарів',
  'doc.goods_recognitions': 'Оприбуткування лишків',
  'doc.goods_returns_from_customer': 'Повернення від покупця',
  'doc.goods_returns_to_supplier': 'Повернення постачальнику',
  'doc.inventory_counts': 'Інвентаризація товарів',
  'doc.warehouse_orders': 'Складський ордер',
  'doc.customs_declarations': 'ВМД (митна декларація)',
  'doc.additional_expenses': 'Додаткові витрати на товар',
  'doc.customer_orders': 'Замовлення від покупця (legacy)',
  'doc.customer_orders.order': 'Замовлення покупця',
  'doc.customer_orders.invoice': 'Рахунок на оплату',
  'doc.supplier_orders': 'Замовлення постачальнику (legacy)',
  'doc.supplier_orders.order': 'Замовлення постачальнику',
  'doc.supplier_orders.invoice': 'Рахунок від постачальника',

  // ===== ОЗ / НМА / МНМА =====
  'ref.fixed_assets_v2': 'Картка ОЗ',
  'ref.intangible_assets_v2': 'Картка НМА',
  'ref.low_value_assets': 'Картка МНМА',
  'doc.asset_commissioning': 'Введення ОЗ в експлуатацію',
  'doc.asset_write_off': 'Акт списання ОЗ',
  'doc.asset_tax_purpose_changes': 'Зміна податкового призначення активу',
  'doc.asset_internal_transfer': 'Переміщення ОЗ',
  'doc.fixed_asset_modernization': 'Модернізація ОЗ',
  'doc.depreciation_calculation': 'Розрахунок амортизації',
  // Legacy / deprecated:
  'doc.fa_commissionings': 'Введення ОЗ (legacy)',
  'doc.fa_depreciations': 'Амортизація (legacy)',
  'doc.fa_disposals': 'Вибуття ОЗ (legacy)',

  // ===== Зарплата / HR =====
  'doc.timesheets': 'Табель робочого часу',
  'doc.daily_timesheets': 'Табель по днях',
  'doc.payrolls': 'Нарахування зарплати',
  'doc.payroll_payments': 'Виплата зарплати',
  'doc.tax_remittances': 'Перерахування податків',
  'doc.salary_deposits': 'Депонування зарплати',
  'doc.fund_receipts': 'Надходження від Фонду',
  'doc.vacation_compensations': 'Компенсація відпустки',
  'doc.civil_law_contracts': 'Договір ЦПХ',
  'doc.hire_notifications': 'Повідомлення про прийняття на роботу',
  'doc.tax_reports': 'Податковий розрахунок (ПДФО, ВЗ, ЄСВ)',
  'doc.dps_requests': 'Запит до ДПС',
  'doc.vacation_orders': 'Наказ на відпустку',
  'doc.vacation_calculations': 'Розрахунок відпускних',
  'doc.sick_leaves': 'Лікарняний лист',
  'doc.sick_leave_calculations': 'Розрахунок лікарняного',
  'doc.hire_orders': 'Прийом на роботу',
  'doc.staff_transfers': 'Кадрове переміщення',
  'doc.dismissal_orders': 'Звільнення',
  'doc.absence_orders': 'Тривала відсутність працівника',

  // ===== ПДВ / Податки =====
  'doc.tax_purpose_adjustments_inventory': 'Зміна призначення запасів (ПДВ)',
  'doc.tax_purpose_adjustments_tzr': 'Зміна призначення ТЗР (ПДВ)',
  'doc.expected_vs_confirmed_vat_adjustments': 'Коригування ПДВ',
  'doc.advance_payment_tax_registration': 'ПН на аванс',
  // 2026-08-06: ПН/РК — послідовні числа ПО ДОКУМЕНТАХ (без дірок для
  // ДПС): префікс не застосовується, зі схеми діють лише період
  // скидання (рік/місяць) і ширина номера.
  'tax.tax_invoices_outgoing': 'ПН (вихідна)',
  'tax.tax_invoices_outgoing.correction': 'РК до ПН',
  'doc.tax_invoice_appendix_1': 'Додаток 1 до ПН',
  'doc.tax_invoice_appendix_2': 'Додаток 2 до ПН',
  'doc.proportional_vat_recalculation': 'Перерахунок пропорційного ПДВ',
  'doc.proportional_vat_coefficient': 'Коефіцієнт пропорційного ПДВ',
  'doc.profit_tax_declarations': 'Декларація з податку на прибуток',
  'doc.financial_statements': 'Фінансова звітність',

  // ===== ФОП =====
  'doc.single_tax_calculations': 'Єдиний податок (ФОП)',
  'doc.fop_self_esv_payments': 'ЄСВ ФОП',
  'doc.fop_self_wz_payments': 'Військовий збір ФОП',
  'doc.fop_zso_quarterly_calculations': 'ЗСО ФОП квартальний',

  // ===== Розрахунки / Заборгованість =====
  'doc.debt_adjustments': 'Взаємозалік / коригування боргу',
  'doc.doubtful_debt_reserves': 'Резерв сумнівних боргів',
  'doc.reconciliation_acts': 'Акт звірки',
  'doc.powers_of_attorney': 'Довіреність',
  'doc.settlement_inventories': 'Інвентаризація розрахунків',

  // ===== Виробництво =====
  'doc.production_reports': 'Звіт виробництва за зміну',
  'doc.product_assemblies': 'Комплектація номенклатури',
  'doc.product_disassemblies': 'Розукомплектація номенклатури',
  'doc.material_transfer_for_processing': 'Передача сировини у переробку',
  'doc.material_receipt_for_processing': 'Прийняття чужої сировини',
  'doc.processing_receipts': 'Надходження з переробки',
  'doc.processing_services_sales': 'Реалізація послуг переробки',
  'doc.production_service_acts': 'Акт виробничих послуг',
  'doc.wip_inventory': 'Інвентаризація НЗВ',

  // ===== Роздріб / POS =====
  'doc.retail_sales': 'Роздрібний продаж',
  'doc.retail_returns': 'Роздрібне повернення',
  'doc.cash_shift_open': 'Відкриття касової зміни',
  'doc.cash_shift_close': 'Закриття касової зміни (Z-звіт)',
  'doc.customer_card_issue': 'Видача дисконтної картки',
  'doc.loyalty_transactions': 'Транзакція бонусів',
  'doc.online_store_order': 'Замовлення з онлайн-магазину',
  'doc.price_change': 'Зміна цін',

  // ===== Договори =====
  'ref.contracts': 'Договір контрагента',

  // ===== Комісія =====
  'ref.commission_contract': 'Договір комісії',
  'doc.commission_goods_transfer': 'Передача товарів на комісію',
  'doc.commission_sale': 'Комісійний продаж',
  'doc.commission_settlement_report': 'Звіт комітента',

  // ===== Системні =====
  'doc.opening_balances': 'Вступні залишки',
  'doc.month_close_runs': 'Закриття місяця',
};

const RESET_LABELS: Record<ResetPeriod, string> = {
  NEVER: 'Ніколи (наскрізна)',
  YEAR: 'Щороку',
  MONTH: 'Щомісяця',
};

// ч.1367: нова схема починається з номера за замовчуванням («26-00001») — користувач
// змінює лише те, що йому потрібно (префікс, повний рік, місяць, код організації).
function emptyEditing(docType: string): Editing {
  return {
    document_type: docType,
    organization_id: null,
    ...DEFAULT_NUMBER_FORMAT,
    reset_period: 'YEAR',
    per_organization: true,
    is_active: true,
  };
}

export default function NumberSchemesPage() {
  const confirm = useConfirm();
  const qc = useQueryClient();
  const docTypesQ = useQuery({
    queryKey: ['doc-types'],
    queryFn: numberSchemes.listDocumentTypes,
  });
  const schemesQ = useQuery({
    queryKey: ['number-schemes'],
    queryFn: () => numberSchemes.list(),
  });
  const orgsQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const [editing, setEditing] = useState<Editing | null>(null);
  const [error, setError] = useState<string | null>(null);

  const orgMap = useMemo(() => {
    const m = new Map<string, string>();
    orgsQ.data?.items.forEach((o) => m.set(o.id, `${o.name} — ${o.code}`));
    return m;
  }, [orgsQ.data]);
  const codeMap = useMemo(
    () => new Map((orgsQ.data?.items ?? []).map((o) => [o.id, o.code ?? ''])), [orgsQ.data]);
  // Код організації у прикладі номера (якщо в схемі «додавати код організації»).
  const orgCode = (id: string | null) => (id && codeMap.get(id)) || 'ORG';

  const save = useMutation({
    // ч.1226: лічильник завжди окремий для організації — сервер іншого не приймає
    mutationFn: () => editing!.id
      ? numberSchemes.update(editing!.id, { ...editing!, per_organization: true })
      : numberSchemes.create({ ...editing!, per_organization: true }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['number-schemes'] });
      setEditing(null);
      setError(null);
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => numberSchemes.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['number-schemes'] }),
  });

  // Preview live при редактировании
  const previewQ = useQuery({
    queryKey: ['preview', editing?.document_type, editing?.organization_id, editing?.prefix,
               editing?.include_year, editing?.year_digits, editing?.include_month, editing?.include_org_code,
               editing?.counter_width, editing?.separator,
               editing?.reset_period, editing?.per_organization, editing?.id],
    queryFn: () => editing?.id
      ? numberSchemes.preview({
          document_type: editing.document_type,
          organization_id: editing.organization_id ?? undefined,
        })
      : Promise.resolve(null),
    enabled: !!editing?.id,
  });

  // Сортуємо за людиночитаною назвою (а не за системним кодом),
  // щоб дропдоун був інтуїтивний для бухгалтера.
  const docTypes = useMemo(() => {
    const list = docTypesQ.data ?? [];
    return [...list].sort((a, b) => {
      const labelA = DOC_TYPE_LABELS[a] ?? a;
      const labelB = DOC_TYPE_LABELS[b] ?? b;
      return labelA.localeCompare(labelB, 'uk');
    });
  }, [docTypesQ.data]);

  // Колонки списку схем нумерації — у спільному стилі DataTable.
  const cols = useMemo<ColumnDef<NumberScheme>[]>(() => [
    {
      key: 'document_type',
      header: 'Тип документа',
      sortValue: (s) => DOC_TYPE_LABELS[s.document_type] ?? s.document_type,
      cell: (s) => DOC_TYPE_LABELS[s.document_type] ?? s.document_type,
    },
    {
      key: 'organization',
      header: 'Організація',
      sortValue: (s) =>
        s.organization_id
          ? (orgMap.get(s.organization_id) ?? s.organization_id)
          : '',
      cell: (s) =>
        s.organization_id
          ? (orgMap.get(s.organization_id) ?? s.organization_id.slice(0, 8))
          : <em style={{ color: 'var(--muted)' }}>—</em>,
    },
    {
      key: 'prefix',
      header: 'Префікс',
      width: 110,
      sortValue: (s) => s.prefix ?? '',
      cell: (s) => <span style={{ fontFamily: 'monospace' }}>{s.prefix || '—'}</span>,
    },
    {
      key: 'format',
      header: 'Вигляд номера',
      sortValue: (s) => numberExample(s, { orgCode: orgCode(s.organization_id) }),
      cell: (s) => (
        <span style={{ fontFamily: 'monospace' }}>
          {numberExample(s, { orgCode: orgCode(s.organization_id) })}
        </span>
      ),
    },
    {
      key: 'reset',
      header: 'Скид',
      width: 130,
      sortValue: (s) => RESET_LABELS[s.reset_period as ResetPeriod],
      cell: (s) => RESET_LABELS[s.reset_period as ResetPeriod],
    },
    {
      key: 'is_active',
      header: 'Активна',
      width: 90,
      align: 'center',
      sortValue: (s) => (s.is_active ? 1 : 0),
      cell: (s) => (s.is_active ? '✓' : '—'),
    },
    {
      key: 'actions',
      header: '',
      width: 110,
      align: 'right',
      cell: (s) => (
        <>
          <button onClick={() => setEditing({ ...s } as Editing)}>змінити</button>
          <button className="danger" style={{ marginLeft: '0.3rem' }}
                  onClick={() => confirm('Видалити схему?').then((ok) => { if (ok) remove.mutate(s.id); })}>
            ×
          </button>
        </>
      ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps -- orgCode читає codeMap
  ], [orgMap, codeMap, confirm, remove]);

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Налаштування нумерації документів</h2>
        <button className="primary"
                onClick={() => setEditing(emptyEditing(docTypes[0] ?? 'doc.timesheets'))}>
          + нова схема
        </button>
      </div>

      <p style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
        Без схеми номер документа має вигляд <code>{numberExample(DEFAULT_NUMBER_FORMAT)}</code>:
        дві цифри року і порядковий номер. Щороку нумерація починається з одиниці, окремо для
        кожної організації і кожного виду документа. Схема потрібна, лише якщо хочете інший
        вигляд — з префіксом, повним роком, місяцем або кодом організації.
      </p>

      {editing && (
        <div style={{ background: 'var(--bg-soft-2)', padding: '1rem',
                      borderRadius: 'var(--radius-lg)', marginBottom: '1rem',
                      border: '1px solid var(--border)' }}>
          <h3 style={{ marginTop: 0 }}>
            {editing.id ? 'Редагування схеми' : 'Нова схема'}
          </h3>
          <div className="doc-grid">
            <div className="field" style={{ gridColumn: 'span 6' }}>
              <label>Тип документа</label>
              <select value={editing.document_type}
                      disabled={!!editing.id}
                      onChange={(e) => setEditing({ ...editing, document_type: e.target.value })}>
                {docTypes.map((dt) => (
                  <option key={dt} value={dt}>{DOC_TYPE_LABELS[dt] ?? dt}</option>
                ))}
              </select>
            </div>
            <div className="field" style={{ gridColumn: 'span 6' }}>
              <label>Організація</label>
              <select value={editing.organization_id ?? ''}
                      disabled={!!editing.id}
                      onChange={(e) => setEditing({ ...editing, organization_id: e.target.value || null })}>
                {/* ч.1225: схема задається для конкретної організації. «Глобальна (для всіх)»
                    діяла б на організації ВСІХ акаунтів — сервер таку більше не приймає. */}
                <option value="">— оберіть організацію —</option>
                {orgsQ.data?.items.map((o) => (
                  <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
                ))}
              </select>
            </div>
            <div className="field" style={{ gridColumn: 'span 6' }}>
              <label>Префікс</label>
              <input value={editing.prefix}
                     placeholder="без префікса"
                     onChange={(e) => setEditing({ ...editing, prefix: e.target.value })} />
              <div style={{ fontSize: '0.7rem', color: 'var(--muted)', marginTop: '0.2rem' }}>
                Порожньо — номер без префікса. Наприклад, «РН» дасть{' '}
                {numberExample({ ...editing, prefix: 'РН' }, { orgCode: orgCode(editing.organization_id) })}.
              </div>
            </div>
            <div className="field" style={{ gridColumn: 'span 6' }}>
              <label>Роздільник</label>
              <input value={editing.separator}
                     onChange={(e) => setEditing({ ...editing, separator: e.target.value })} />
            </div>
            <div className="field" style={{ gridColumn: 'span 6' }}>
              <label>Ширина лічильника</label>
              <input type="number" min={1} max={10} value={editing.counter_width}
                     onChange={(e) => setEditing({ ...editing, counter_width: Number(e.target.value) })} />
            </div>
            <div className="field" style={{ gridColumn: 'span 6' }}>
              <label>Рік у номері</label>
              <select value={editing.year_digits} disabled={!editing.include_year}
                      onChange={(e) => setEditing({ ...editing, year_digits: Number(e.target.value) as 2 | 4 })}>
                <option value={2}>двома цифрами (26)</option>
                <option value={4}>чотирма цифрами (2026)</option>
              </select>
            </div>
            <div className="field" style={{ gridColumn: 'span 6' }}>
              <label>Скид лічильника</label>
              <select value={editing.reset_period}
                      onChange={(e) => setEditing({ ...editing, reset_period: e.target.value as ResetPeriod })}>
                {(['NEVER','YEAR','MONTH'] as ResetPeriod[]).map((p) =>
                  <option key={p} value={p}>{RESET_LABELS[p]}</option>
                )}
              </select>
            </div>
            <div className="field" style={{ gridColumn: 'span 12' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <input type="checkbox" checked={editing.include_year} style={{ width: 'auto' }}
                       onChange={(e) => setEditing({ ...editing, include_year: e.target.checked })} />
                Додавати рік
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <input type="checkbox" checked={editing.include_month} style={{ width: 'auto' }}
                       onChange={(e) => setEditing({ ...editing, include_month: e.target.checked })} />
                Додавати місяць
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <input type="checkbox" checked={editing.include_org_code} style={{ width: 'auto' }}
                       onChange={(e) => setEditing({ ...editing, include_org_code: e.target.checked })} />
                Додавати код організації
              </label>
              {/* ч.1226: галочку «Окрема нумерація для кожної організації» прибрано — лічильник
                  завжди окремий (спільний діяв би на організації всіх акаунтів). */}
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <input type="checkbox" checked={editing.is_active} style={{ width: 'auto' }}
                       onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} />
                Активна
              </label>
            </div>
          </div>

          <p style={{ marginTop: '0.5rem', padding: '0.5rem 0.75rem',
                       background: 'var(--primary-soft)', color: 'var(--primary)',
                       borderRadius: 'var(--radius-sm)' }}>
            Вигляд номера: <strong style={{ fontFamily: 'monospace' }}>
              {numberExample(editing, { orgCode: orgCode(editing.organization_id) })}
            </strong>
            {previewQ.data && (
              <> · наступний за збереженою схемою: <strong style={{ fontFamily: 'monospace' }}>
                {previewQ.data.preview}</strong></>
            )}
          </p>

          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
            <button className="primary" onClick={() => save.mutate()}
                    disabled={save.isPending || !editing.organization_id}
                    title={!editing.organization_id ? 'Спершу оберіть організацію' : undefined}>
              зберегти
            </button>
            <button onClick={() => { setEditing(null); setError(null); }}>скасувати</button>
          </div>
          {error && <div className="error">{error}</div>}
        </div>
      )}

      <DataTable<NumberScheme>
        rows={schemesQ.data ?? []}
        columns={cols}
        rowKey={(s) => s.id}
        loading={schemesQ.isLoading}
        emptyText={`Схем немає — документи нумеруються як ${numberExample(DEFAULT_NUMBER_FORMAT)}, ${numberExample(DEFAULT_NUMBER_FORMAT, { value: 2 })}… Додайте схему, якщо потрібен інший вигляд номера.`}
      />
    </div>
  );
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}
